import { Button } from "@/components/ui/button";
import { useAdminPerms } from "@/hooks/auth";
import { PlayerModalRefType, useClosePlayerModal } from "@/hooks/playerModal";
import { Loader2Icon } from "lucide-react";
import { useRef, useState } from "react";
import { useBackendApi } from "@/hooks/fetch";
import { GenericApiOkResp } from "@shared/genericApiTypes";
import type { SanctionTemplateType } from "@shared/otherTypes";
import type { PlayerModalPlayerData } from "@shared/playerApiTypes";
import { findActivePlayerAction } from "@shared/utils";
import { tsToLocaleDateTimeString } from "@/lib/dateTime";
import { ShieldAlertIcon } from "lucide-react";
import BanForm, { BanFormType } from "@/components/BanForm";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { txToast } from "@/components/TxToaster";
import { ModalTabInner, ModalTabMessage } from "@/components/modal-tabs";


type PlayerBanTabProps = {
    banTemplates: SanctionTemplateType[];
    playerRef: PlayerModalRefType;
    player: PlayerModalPlayerData;
    serverTime: number;
};

export default function PlayerBanTab({ playerRef, banTemplates, player, serverTime }: PlayerBanTabProps) {
    const banFormRef = useRef<BanFormType>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isAnticheat, setIsAnticheat] = useState(false);
    const { hasPerm } = useAdminPerms();
    const closeModal = useClosePlayerModal();
    const playerBanApi = useBackendApi<GenericApiOkResp>({
        method: 'POST',
        path: `/player/ban`,
        throwGenericErrors: true,
    });

    const canAnticheat = hasPerm('players.anticheat');

    if (!hasPerm('players.ban')) {
        return <ModalTabMessage>
            You don't have permission to ban players.
        </ModalTabMessage>;
    }

    const handleSave = () => {
        if (!banFormRef.current) return;
        const { reason, duration } = banFormRef.current.getData();

        if (!reason || reason.length < 3) {
            txToast.warning(`The reason must be at least 3 characters long.`);
            banFormRef.current.focusReason();
            return;
        }

        setIsSaving(true);
        playerBanApi({
            queryParams: playerRef,
            data: { reason, duration, anticheat: isAnticheat },
            toastLoadingMessage: 'Banning player...',
            genericHandler: {
                successMsg: 'Player banned.',
            },
            finally: () => setIsSaving(false),
            success: () => closeModal(),
        });
    };

    const activeBan = findActivePlayerAction(player.actionHistory, 'ban', serverTime);

    return (
        <ModalTabInner className="grid gap-4">
            {activeBan ? (
                <div className="w-full p-2 pr-3 flex items-center justify-between gap-4 rounded-lg border shadow-pop text-white/90 border-warning/70 bg-warning-hint">
                    <ShieldAlertIcon className="size-5 shrink-0 text-warning" />
                    <div className="flex-grow text-sm font-medium">
                        This player is already banned {activeBan.exp
                            ? `until ${tsToLocaleDateTimeString(activeBan.exp, 'medium', 'short')}`
                            : 'permanently'} ({activeBan.id}).<br />
                        <span className="opacity-75">A new ban will be added on top of it.</span>
                    </div>
                </div>
            ) : null}
            <BanForm
                ref={banFormRef}
                banTemplates={banTemplates}
                disabled={isSaving}
                onNavigateAway={() => { closeModal(); }}
                onTemplateSelect={(template) => setIsAnticheat(canAnticheat && !!template.anticheat)}
            />
            <div className="flex items-center justify-between gap-4">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span
                            tabIndex={canAnticheat ? -1 : 0}
                            className={cn('inline-flex', !canAnticheat && 'cursor-not-allowed')}
                        >
                            <label className={cn(
                                'flex items-center gap-2 text-sm select-none',
                                canAnticheat ? 'cursor-pointer' : 'opacity-40 pointer-events-none',
                            )}>
                                <Checkbox
                                    tone="danger"
                                    checked={isAnticheat}
                                    onCheckedChange={(checked) => setIsAnticheat(checked === true)}
                                    disabled={!canAnticheat || isSaving}
                                />
                                Anticheat Ban
                            </label>
                        </span>
                    </TooltipTrigger>
                    {!canAnticheat ? (
                        <TooltipContent className="text-anticheat-inline text-center">
                            <p>
                                You need the "Anticheat Sanctions" permission<br />
                                to flag a ban as an anticheat ban.
                            </p>
                        </TooltipContent>
                    ) : null}
                </Tooltip>
                <Button
                    size="sm"
                    variant="destructive"
                    disabled={isSaving}
                    onClick={handleSave}
                >
                    {isSaving ? (
                        <span className="flex items-center leading-relaxed">
                            <Loader2Icon className="inline animate-spin h-4" /> Banning...
                        </span>
                    ) : 'Apply Ban'}
                </Button>
            </div>
        </ModalTabInner>
    );
}
